import React, { useState } from 'react'
import { Layout, Menu, Button, Dropdown, Avatar, Space, Drawer } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  DashboardOutlined,
  FileTextOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/renderer/hooks'
import { logout } from '@/renderer/store/authSlice'

const { Header, Sider, Content } = Layout

interface MainLayoutProps {
  children: React.ReactNode
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const { user } = useAppSelector((state) => state.auth)

  const handleLogout = async () => {
    await dispatch(logout())
    navigate('/login')
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ]

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'materials',
      icon: <BuildOutlined />,
      label: 'Materials',
      onClick: () => navigate('/materials/requests'),
      children: [
        {
          key: 'material-requests',
          label: 'Requests',
          onClick: () => navigate('/materials/requests'),
        },
        {
          key: 'shipping-lists',
          label: 'Shipping Lists',
          onClick: () => navigate('/materials/shipping'),
        },
        {
          key: 'toolstrings',
          label: 'Toolstrings',
          onClick: () => navigate('/materials/toolstrings'),
        },
      ],
    },
    {
      key: 'operations',
      icon: <FileTextOutlined />,
      label: 'Operations',
      onClick: () => navigate('/operations/daily-reports'),
      children: [
        {
          key: 'daily-reports',
          label: 'Daily Reports',
          onClick: () => navigate('/operations/daily-reports'),
        },
        {
          key: 'time-logs',
          label: 'Time Logs',
          onClick: () => navigate('/operations/time-logs'),
        },
        {
          key: 'ris-reports',
          label: 'RIS Reports',
          onClick: () => navigate('/operations/ris'),
        },
      ],
    },
    {
      key: 'certificates',
      icon: <CheckCircleOutlined />,
      label: 'Certificates',
      onClick: () => navigate('/certificates'),
    },
    {
      key: 'inventory',
      icon: <ToolOutlined />,
      label: 'Inventory',
      children: [
        {
          key: 'tools',
          label: 'Tools',
          onClick: () => navigate('/inventory/tools'),
        },
      ],
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
      onClick: () => navigate('/analytics'),
    },
  ]

  const drawerMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => {
        navigate('/dashboard')
        setMobileDrawerOpen(false)
      },
    },
    {
      key: 'materials',
      icon: <BuildOutlined />,
      label: 'Materials',
    },
    {
      key: 'operations',
      icon: <FileTextOutlined />,
      label: 'Operations',
    },
    {
      key: 'certificates',
      icon: <CheckCircleOutlined />,
      label: 'Certificates',
      onClick: () => {
        navigate('/certificates')
        setMobileDrawerOpen(false)
      },
    },
    {
      key: 'inventory',
      icon: <ToolOutlined />,
      label: 'Inventory',
      onClick: () => {
        navigate('/inventory/tools')
        setMobileDrawerOpen(false)
      },
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
      onClick: () => {
        navigate('/analytics')
        setMobileDrawerOpen(false)
      },
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          display: 'none',
        }}
        className="hidden md:flex"
        width={250}
      >
        <div style={{ padding: '16px', color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
          {!collapsed && 'EXPRO'}
        </div>
        <Menu theme="dark" mode="inline" items={menuItems} />
      </Sider>

      {/* Mobile Drawer */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        className="md:hidden"
      >
        <Menu mode="vertical" items={drawerMenuItems} onClick={() => setMobileDrawerOpen(false)} />
      </Drawer>

      <Layout>
        {/* Header */}
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex"
              style={{ fontSize: '16px' }}
            />
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              className="md:hidden"
              style={{ fontSize: '16px' }}
            />
          </div>

          <Space>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span className="hidden sm:inline">{user?.email}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* Content */}
        <Content
          style={{
            margin: '16px',
            padding: '24px',
            background: '#f5f5f5',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}
